import { Pause, PlayArrow, VideoFile as VideoIcon } from '@mui/icons-material';
import { Box, CircularProgress, IconButton, Typography } from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';
import { normalizeUrl } from '../../services/urlNormalizationService';
import { Asset } from '../../types/asset.types';
import { generateVideoThumbnail, isVideoUrl } from '../../utils/videoThumbnail';
import EnhancedLayerIcon from './EnhancedLayerIcon';

// Global cache for video thumbnails to persist across re-renders
const thumbnailCache = new Map<string, string>();

interface VideoThumbnailProps {
  asset: Asset;
  width?: number;
  height?: number;
  showFallbackIcon?: boolean;
}

/**
 * Enhanced component that displays video thumbnails with robust error handling
 */
const VideoThumbnail: React.FC<VideoThumbnailProps> = ({
  asset,
  width = 40,
  height = 40,
  showFallbackIcon = true,
}) => {
  // Use ref to track if component is mounted to prevent state updates after unmount
  const isMountedRef = useRef(true);

  // Get media URL from canonical fileUrl first, then legacy videoUrl fields.
  const rawVideoUrl =
    asset.metadata?.fileUrl ??
    asset.metadata?.videoUrl ??
    (asset as any).fileUrl ??
    (asset as any).videoUrl;
  const normalizedVideoUrl = normalizeUrl(rawVideoUrl);
  const isVideoAsset =
    normalizedVideoUrl &&
    (isVideoUrl(normalizedVideoUrl) ||
      asset.layer === 'M' ||
      asset.layer === 'W' ||
      asset.layer === 'C');
  // Initialize state from cache if available
  const [thumbnailUrl, setThumbnailUrl] = useState<string | null>(() => {
    return isVideoAsset ? thumbnailCache.get(normalizedVideoUrl) || null : null;
  });
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [hasError, setHasError] = useState<boolean>(false);
  const [loadingProgress, setLoadingProgress] = useState<string>('');

  // Video playback state
  const [isPlaying, setIsPlaying] = useState(false);
  const [showVideo, setShowVideo] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const videoRef = useRef<HTMLVideoElement | null>(null);

  // Clean up on unmount
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      // Stop video playback on unmount
      if (videoRef.current) {
        videoRef.current.pause();
        videoRef.current = null;
      }
    };
  }, []);

  // Generate thumbnail when component mounts
  useEffect(() => {
    if (!isVideoAsset) {
      return;
    }

    // Check cache first
    const cachedThumbnail = thumbnailCache.get(normalizedVideoUrl);
    if (cachedThumbnail) {
      console.log(`✅ Using cached thumbnail for ${asset.name}`);
      if (isMountedRef.current) {
        setThumbnailUrl(cachedThumbnail);
        setIsLoading(false);
        setHasError(false);
      }
      return;
    }

    // Start thumbnail generation
    console.log(
      `🎬 Starting thumbnail generation for ${asset.name}: ${normalizedVideoUrl}`
    );

    if (isMountedRef.current) {
      setIsLoading(true);
      setHasError(false);
      setLoadingProgress('Loading video...');
    }

    generateVideoThumbnail(normalizedVideoUrl)
      .then(dataUrl => {
        console.log(`✅ Successfully generated thumbnail for ${asset.name}`);

        // Cache the thumbnail globally using normalized URL
        thumbnailCache.set(normalizedVideoUrl, dataUrl);

        if (isMountedRef.current) {
          setThumbnailUrl(dataUrl);
          setIsLoading(false);
          setLoadingProgress('');
        }
      })
      .catch(error => {
        console.warn(
          `❌ Failed to generate thumbnail for ${asset.name}:`,
          error.message
        );

        if (isMountedRef.current) {
          setHasError(true);
          setIsLoading(false);
          setLoadingProgress('');
        }
      });
  }, [normalizedVideoUrl, asset.name, isVideoAsset]);

  // Handle video playback toggle
  const handlePlayPause = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent card click navigation

    if (!normalizedVideoUrl) return;

    // If clicking for the first time, show video element and start playing
    if (!showVideo) {
      setShowVideo(true);
      setIsPlaying(true);
      // Video will autoplay via the useEffect below
      return;
    }

    // Toggle play/pause for existing video
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
        setIsPlaying(false);
      } else {
        videoRef.current.play().catch((error) => {
          console.error('🎬 [VIDEO PLAYBACK] Play failed:', error);
          setIsPlaying(false);
        });
        setIsPlaying(true);
      }
    }
  };

  // Auto-play when video element is created
  useEffect(() => {
    if (showVideo && videoRef.current && isPlaying) {
      videoRef.current.play().catch((error) => {
        console.error('🎬 [VIDEO PLAYBACK] Auto-play failed:', error);
        setIsPlaying(false);
      });
    }
  }, [showVideo, isPlaying]);

  const containerStyle = {
    width,
    height,
    borderRadius: 1,
    overflow: 'hidden',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'grey.100',
    position: 'relative' as const,
  };

  // Show loading state with progress indicator
  if (isLoading) {
    return (
      <Box sx={containerStyle}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 0.5,
          }}
        >
          <CircularProgress size={Math.min(width, height) * 0.4} />
          {width >= 80 && loadingProgress && (
            <Typography
              variant="caption"
              sx={{
                fontSize: '0.6rem',
                color: 'text.secondary',
                textAlign: 'center',
              }}
            >
              {loadingProgress}
            </Typography>
          )}
        </Box>
      </Box>
    );
  }

  // Show generated thumbnail with video playback
  if (thumbnailUrl && !hasError) {
    return (
      <Box
        sx={{
          ...containerStyle,
          position: 'relative',
          cursor: 'pointer',
        }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {/* Show video when playing, thumbnail otherwise */}
        {showVideo && normalizedVideoUrl ? (
          <video
            ref={videoRef}
            src={normalizedVideoUrl}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              borderRadius: '4px',
            }}
            onEnded={() => {
              setIsPlaying(false);
              setShowVideo(false); // Hide video and show thumbnail again
            }}
            onError={() => {
              console.error('🎬 [VIDEO PLAYBACK] Failed to load video:', normalizedVideoUrl);
              setIsPlaying(false);
              setShowVideo(false);
            }}
          />
        ) : (
          <img
            src={thumbnailUrl}
            alt={`${asset.friendlyName || asset.name} thumbnail`}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              borderRadius: '4px',
            }}
            onError={e => {
              console.warn(`Thumbnail failed to load for ${asset.name}`);
              setHasError(true);
            }}
          />
        )}

        {/* Play/Pause Button Overlay */}
        {(isHovered || isPlaying) && (
          <IconButton
            onClick={handlePlayPause}
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              backgroundColor: 'rgba(0, 0, 0, 0.6)',
              color: 'white',
              width: Math.max(width * 0.4, 24),
              height: Math.max(height * 0.4, 24),
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.8)',
                transform: 'translate(-50%, -50%) scale(1.1)',
              },
              transition: 'all 0.2s ease',
            }}
          >
            {isPlaying ? (
              <Pause sx={{ fontSize: Math.max(width * 0.25, 16) }} />
            ) : (
              <PlayArrow sx={{ fontSize: Math.max(width * 0.25, 16) }} />
            )}
          </IconButton>
        )}

        {/* Video indicator overlay (only show when not playing) */}
        {!showVideo && (
          <Box
            sx={{
              position: 'absolute',
              bottom: 2,
              right: 2,
              backgroundColor: 'rgba(0,0,0,0.6)',
              borderRadius: '2px',
              padding: '1px 3px',
            }}
          >
            <VideoIcon sx={{ fontSize: 8, color: 'white' }} />
          </Box>
        )}
      </Box>
    );
  }

  // Video layers (C/M/W) with URL but thumbnail failed: show icon + play overlay so user can still play
  const isVideoLayer = asset.layer === 'M' || asset.layer === 'W' || asset.layer === 'C';
  if (
    hasError &&
    normalizedVideoUrl &&
    isVideoLayer &&
    showFallbackIcon
  ) {
    return (
      <Box
        sx={{
          ...containerStyle,
          position: 'relative',
          cursor: 'pointer',
        }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <EnhancedLayerIcon
          layer={asset.layer}
          width={width}
          height={height}
          showLabel={width >= 60}
        />
        {showVideo && normalizedVideoUrl ? (
          <video
            ref={videoRef}
            src={normalizedVideoUrl}
            style={{
              position: 'absolute',
              inset: 0,
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              borderRadius: '4px',
            }}
            onEnded={() => {
              setIsPlaying(false);
              setShowVideo(false);
            }}
            onError={() => {
              console.error('🎬 [VIDEO PLAYBACK] Failed to load video:', normalizedVideoUrl);
              setIsPlaying(false);
              setShowVideo(false);
            }}
          />
        ) : null}
        {(isHovered || isPlaying) && (
          <IconButton
            onClick={handlePlayPause}
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              backgroundColor: 'rgba(0, 0, 0, 0.6)',
              color: 'white',
              width: Math.max(width * 0.4, 24),
              height: Math.max(height * 0.4, 24),
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.8)',
                transform: 'translate(-50%, -50%) scale(1.1)',
              },
              transition: 'all 0.2s ease',
            }}
          >
            {isPlaying ? (
              <Pause sx={{ fontSize: Math.max(width * 0.25, 16) }} />
            ) : (
              <PlayArrow sx={{ fontSize: Math.max(width * 0.25, 16) }} />
            )}
          </IconButton>
        )}
      </Box>
    );
  }

  // Show enhanced fallback icon ONLY for errors or non-video assets
  // Don't show fallback if we're still loading or if this is a video that should have a thumbnail
  if (showFallbackIcon && !isLoading && (!isVideoAsset || hasError)) {
    return (
      <EnhancedLayerIcon
        layer={asset.layer}
        width={width}
        height={height}
        showLabel={width >= 60} // Only show label for larger icons
      />
    );
  }

  // Return a loading placeholder for video assets that are generating thumbnails
  if (isVideoAsset && !thumbnailUrl && !hasError) {
    return (
      <Box sx={containerStyle}>
        <CircularProgress size={Math.min(width, height) * 0.4} />
      </Box>
    );
  }

  // Return null if no fallback requested
  return null;
};

export default VideoThumbnail;
